import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { List } from "@remo-co/ui-core/src/components/List";
import { ListItem } from "@remo-co/ui-core/src/components/ListItem";
import { ListItemAvatar } from "@remo-co/ui-core/src/components/ListItemAvatar";
import { ListItemSecondaryAction } from "@remo-co/ui-core/src/components/ListItemSecondaryAction";
import { ListItemText } from "@remo-co/ui-core/src/components/ListItemText";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ChevronRight as ChevronRightIcon } from "@remo-co/ui-core/src/icons/ChevronRight";
import { useHistory } from "react-router";
import moment from "moment";
import { getVar } from "config";
import {
  getChargebeePortalSession,
  getChargebeeSubscriptionDetail,
} from "services/apiService/apis";
import useHelpChatActions from "modules/helpChat/hooks/useHelpChatActions";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { usePrevious } from "helpers/reactHooksUtils";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isMyCompanyTrialPeriodActive } from "modules/companyPlanSettings/utils/trial";
import { useI18n } from "i18n";
import { add } from "helpers/time/operations";
import { isAfter } from "helpers/time/comparison";
import { ISubscriptionDetail } from "types/subscription";
import { selectUser } from "modules/auth/redux/selectors";
import { IHistoryLocation } from "types/history";
import { ReactComponent as EditIcon } from "./assets/edit-inline.svg";
import { ReactComponent as AccountIcon } from "./assets/my-profile.inline.svg";
import { ReactComponent as TimeIcon } from "./assets/time.inline.svg";
import { ReactComponent as CreditCardIcon } from "./assets/credit-card.inline.svg";
import "./PlanBilling.scss";
import logger from "../../logging/logger";
import useCompany from "../company/hooks/useCompany";
import PlanDetails from "./PlanDetails";
import PlanChange from "./PlanChange";
import subscriptionApi from "./Subscription.api";
import { SUBSCRIPTION_IN_TRIAL, SUBSCRIPTION_NON_RENEWING } from "./constants";
import { useStyles } from "./styles";
import { CancelSubscriptionListItem } from "./components";
import { AppSumoProductsLink } from "./constants/AppSumoLinks";

const PlanBilling = (): JSX.Element => {
  const { Chargebee } = window;
  const [subscriptionDetail, setSubscriptionDetail] =
    useState<ISubscriptionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const user = useSelector(selectUser);
  const { t } = useI18n(["common", "settings", "server", "url"]);
  const { location } = useHistory<IHistoryLocation>();
  const isOpen = location.state && location.state.open;
  const [isShowPlans, setIsShowPlans] = useState(isOpen);
  const [isShowBillingHistory] = useState(
    location.state && location.state.history,
  );
  const cbInstance = Chargebee.init({
    site: getVar("REACT_APP_CHARGEBEE_SITE"),
    enableGATracking: true,
  });
  const cbPortal = cbInstance.createChargebeePortal();
  const { loadCompany, company } = useCompany();
  const { myCompanyPlan, isAppsumoPlan } = useCompanyPlanSettings();
  const { serverTime } = useSystemClock();
  const { pushEvents } = useHelpChatActions();
  const prevSubscriptionDetail = usePrevious(subscriptionDetail);

  const [isReactivatingSubscription, setIsReactivatingSubscription] =
    useState(false);
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { track } = useContext(TRACKING_CONTEXT);

  const onSubscriptionChange = (data: ISubscriptionDetail) => {
    logger.info("[PlanBilling] subscription changed in portal.", data);

    if (data.subscription && data.subscription.id) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      refreshSubscriptionInfo();
    }
  };

  const callbacks = {
    subscriptionCancelled: onSubscriptionChange,
    subscriptionReactivated: onSubscriptionChange,
    subscriptionChanged: onSubscriptionChange,
  };
  const handleViewAccountDetails = () => {
    cbPortal.open(callbacks, {
      sectionType: Chargebee.getPortalSections().ACCOUNT_DETAILS,
    });
  };

  const handleViewBillingHistory = () => {
    cbPortal.open(callbacks, {
      sectionType: Chargebee.getPortalSections().BILLING_HISTORY,
    });
  };

  const handleViewPaymentMethod = () => {
    cbPortal.open(callbacks, {
      sectionType: Chargebee.getPortalSections().PAYMENT_SOURCES,
    });
  };

  const handleEditSubscription = () => {
    cbPortal.open(callbacks, {
      sectionType: Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
      params: {
        subscriptionId: "active_direct",
      },
    });
  };

  const handleChangePlan = () => {
    handleEditSubscription();
  };

  const handleUpgradePlan = () => {
    setIsShowPlans(true);
  };

  const handleReactivatePlan = async () => {
    setIsReactivatingSubscription(true);

    const data = {
      subscriptionId: subscriptionDetail?.subscription?.id,
    };

    const result = await subscriptionApi.reactivateUserSubscription(data);

    if (result.isSuccess) {
      logger.info(
        `[handleReactivatePlan] Subscription reactivated for ${subscriptionDetail?.subscription?.id}`,
      );
      addSuccessNotification({
        message: t("settings:subscription.reactivate"),
        position: "tc",
        autoDismiss: 10,
      });
      track(Events.SUBSCRIPTION_REACTIVATED, {
        plan: subscriptionDetail?.plan?.name || "N/A",
      });
    } else {
      logger.error(
        `[handleReactivatePlan] Unable to reactivate subscription ${subscriptionDetail?.subscription?.id}`,
        { result },
      );
      addErrorNotification({
        message: t(result.message) || t("error.unexpected"),
        position: "tc",
        autoDismiss: 10,
      });
    }

    setIsReactivatingSubscription(false);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    refreshSubscriptionInfo();
  };

  const handleTrialUpgrade = () => {
    const keys = user
      ? {
          email: user.email,
          firstName: user.profile.firstName,
          lastName: user.profile.lastName,
        }
      : undefined;

    window.open(t("url:upgrade.now.form", keys), "_blank");
  };

  const refreshSubscriptionInfo = () => {
    setIsLoading(true);
    // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
    getChargebeeSubscriptionDetail().then((result) => {
      // eslint-disable-next-line promise/always-return
      if (result.isSuccess) {
        setSubscriptionDetail({
          subscription: result.subscription,
          plan: result.plan,
          customer: result.customer,
        });
        loadCompany();
      }
      setIsLoading(false);
    });
    setIsShowPlans(false);
  };

  const isRecentlyHappened = (time: number) => {
    const occurredTime = moment(time);
    const timeLimit = add(serverTime, { minute: -2 });

    return isAfter(occurredTime, timeLimit);
  };

  const handleSubscriptionChange = (
    curSubscription: ISubscriptionDetail | null,
    prevSubscription?: ISubscriptionDetail | null,
  ) => {
    if (
      company &&
      curSubscription &&
      curSubscription.subscription &&
      curSubscription.plan
    ) {
      const { subscription } = curSubscription;
      const { status } = subscription;

      if (status !== "active") {
        return;
      }

      if (
        isRecentlyHappened(subscription.started_at * 1000) ||
        isRecentlyHappened(subscription.updated_at * 1000)
      ) {
        pushEvents("rc_company_paid", {
          plan: curSubscription.plan.name,
          time: Math.ceil(serverTime),
          mrr: curSubscription.subscription.mrr,
          amount: curSubscription.subscription.plan_amount,
          prevPlan: prevSubscription?.plan.name,
        });
      }

      return;
    }

    if (company && prevSubscription && prevSubscription.plan) {
      const { name } = prevSubscription.plan;

      pushEvents("rc_company_cancelled", {
        previousPlan: name,
        time: Math.ceil(serverTime),
      });
    }
  };

  useEffect(() => {
    // When user is changing plans, webhooks might be delayed sometimes to update DB
    // So when loading billing info, if company plan name and chargebee details are not matched, load plan again
    // BY this time, our DB will be updated.
    if (
      subscriptionDetail &&
      subscriptionDetail.plan &&
      myCompanyPlan &&
      (subscriptionDetail.plan.name !== myCompanyPlan.name || // If different plan
        isMyCompanyTrialPeriodActive(myCompanyPlan)) // Or in trial or trial expired
    ) {
      loadCompany();
    }

    handleSubscriptionChange(subscriptionDetail, prevSubscriptionDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionDetail]);

  useEffect(() => {
    try {
      logger.info("[PlanBilling] Setting up Chargebee portal session", {
        user,
      });
      cbInstance.setPortalSession(
        () =>
          new Promise((resolve) => {
            // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
            getChargebeePortalSession().then((result) => {
              // eslint-disable-next-line promise/always-return
              if (result.isSuccess) {
                logger.info(
                  `[PlanBilling] Chargebee portal session loaded for user ${user?.id}`,
                );
                resolve(result.portal_session);
              } else {
                logger.error(
                  `[PlanBilling] Setting up Chargebee portal session failed to load for user ${user?.id}`,
                );
                resolve(null);
              }
            });
          }),
      );
      logger.info("[PlanBilling] Acquiring subscription info to display", {
        user,
      });
      // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
      getChargebeeSubscriptionDetail().then((result) => {
        if (result.isSuccess) {
          setSubscriptionDetail({
            subscription: result.subscription,
            plan: result.plan,
            customer: result.customer,
          });
        }
        logger.info(
          // eslint-disable-next-line promise/always-return
          `[PlanBilling] Subscription data loaded for user ${user?.id}`,
          { result },
        );
        setIsLoading(false);
      });
    } catch (error) {
      logger.info(
        `[PlanBilling] Initialization failed, error occurred for user ${user?.id}`,
        { error },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowBillingHistory) {
      handleViewBillingHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowBillingHistory]);

  const styles = useStyles();

  const handleManageAppSumoLicense = () =>
    window.open(AppSumoProductsLink, "_blank");

  return (
    <div className="plan-billing-container">
      <PlanDetails
        isLoading={isLoading}
        subscriptionDetail={subscriptionDetail}
      />
      {isAppsumoPlan && (
        <div className="block">
          <Button
            variant="primary"
            color="blue"
            size="md"
            onClick={handleManageAppSumoLicense}
          >
            {t("settings:manage.appsumo.license")}
          </Button>
        </div>
      )}

      {!subscriptionDetail && !isAppsumoPlan && (
        <div className="block">
          <Button
            variant="primary"
            color="blue"
            onClick={handleTrialUpgrade}
            size="md"
          >
            {t("settings:plan.upgrade")}
          </Button>
        </div>
      )}

      {subscriptionDetail && !isAppsumoPlan && (
        <>
          <div className="block">
            {(!subscriptionDetail.subscription ||
              subscriptionDetail.subscription.status ===
                SUBSCRIPTION_IN_TRIAL) && (
              <Button
                variant="primary"
                color="blue"
                onClick={handleUpgradePlan}
                size="md"
              >
                {t("settings:plan.upgrade")}
              </Button>
            )}
            {subscriptionDetail.subscription &&
              subscriptionDetail.subscription.status !==
                SUBSCRIPTION_NON_RENEWING && (
                <Button
                  variant="secondary"
                  color="blue"
                  onClick={handleChangePlan}
                  size="md"
                >
                  <Typography variant="body1">
                    {t("settings:plan.change")}
                  </Typography>
                </Button>
              )}
            {!!myCompanyPlan &&
              subscriptionDetail?.subscription?.status ===
                SUBSCRIPTION_NON_RENEWING && (
                <Button
                  variant="primary"
                  color="blue"
                  disabled={isReactivatingSubscription}
                  onClick={handleReactivatePlan}
                  loading={isReactivatingSubscription}
                  size="md"
                >
                  {!isReactivatingSubscription
                    ? "Reactivate Plan"
                    : t("processing")}
                </Button>
              )}
          </div>
          <div className="block">
            <Typography variant="body2" className={styles.option}>
              {t("settings:manage.subscription.option")}
            </Typography>
            <div className="block">
              <List className={styles.billingList}>
                <ListItem
                  button
                  onClick={handleViewAccountDetails}
                  className={styles.billingListItem}
                >
                  <ListItemAvatar className={styles.icon}>
                    <AccountIcon />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography variant="h5">
                      {t("settings:account.info")}
                    </Typography>
                    <Typography variant="caption">
                      {t("settings:update.account.details")}
                    </Typography>
                  </ListItemText>
                  <ListItemSecondaryAction className={styles.chevron}>
                    <ChevronRightIcon />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem
                  button
                  onClick={handleEditSubscription}
                  className={styles.billingListItem}
                  data-testid="edit-subscription"
                >
                  <ListItemAvatar className={styles.icon}>
                    <EditIcon />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography variant="h5">
                      {t("settings:edit.subscription.header")}
                    </Typography>
                    <Typography variant="caption">
                      {t("settings:edit.subscription.caption")}
                    </Typography>
                  </ListItemText>
                  <ListItemSecondaryAction className={styles.chevron}>
                    <ChevronRightIcon />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem
                  className={styles.billingListItem}
                  button
                  onClick={handleViewBillingHistory}
                >
                  <ListItemAvatar className={styles.icon}>
                    <TimeIcon />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography variant="h5">
                      {t("settings:billing.history")}
                    </Typography>
                    <Typography variant="caption">
                      {t("settings:view.purchases")}
                    </Typography>
                  </ListItemText>
                  <ListItemSecondaryAction className={styles.chevron}>
                    <ChevronRightIcon />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem
                  className={styles.billingListItem}
                  button
                  onClick={handleViewPaymentMethod}
                >
                  <ListItemAvatar className={styles.icon}>
                    <CreditCardIcon />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography variant="h5">
                      {t("settings:payment.methods")}
                    </Typography>
                    <Typography variant="caption">
                      {t("settings:manage.payment.methods")}
                    </Typography>
                  </ListItemText>
                  <ListItemSecondaryAction className={styles.chevron}>
                    <ChevronRightIcon />
                  </ListItemSecondaryAction>
                </ListItem>

                <CancelSubscriptionListItem
                  subscriptionDetail={subscriptionDetail}
                  refreshSubscriptionInfo={refreshSubscriptionInfo}
                />
              </List>
            </div>
          </div>
        </>
      )}
      {!isAppsumoPlan && (
        <PlanChange
          handleClose={refreshSubscriptionInfo}
          setIsShowPlans={setIsShowPlans}
          isShowPlans={isShowPlans}
          subscriptionDetail={subscriptionDetail}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default PlanBilling;
